import { Stack, type StackProps, Switch, type SwitchProps, Typography } from "@mui/material";
import { Controller, type FieldPath, type FieldValues, type UseControllerProps } from "react-hook-form";

export type TSwitchFormProps<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
> = Omit<SwitchProps, "name" | "checked" | "onBlur" | "defaultValue"> &
  UseControllerProps<TFieldValues, TName, TTransformedValues> & {
    label: string;
    slotProps?: { stackProps?: StackProps };
  };

export const SwitchForm = <
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
  TTransformedValues = TFieldValues,
>({
  name,
  rules,
  shouldUnregister,
  control,
  label,
  defaultValue,
  onChange,
  slotProps,
  ...props
}: TSwitchFormProps<TFieldValues, TName, TTransformedValues>) => {
  return (
    <Controller
      name={name}
      rules={rules}
      shouldUnregister={shouldUnregister}
      defaultValue={defaultValue}
      control={control}
      render={({ field }) => (
        <Stack
          direction="row"
          {...slotProps?.stackProps}
          sx={{
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography variant="body1" noWrap>
            {label}
          </Typography>
          <Switch
            {...props}
            checked={field.value}
            name={field.name}
            onChange={(...args) => {
              if (onChange) {
                onChange(...args);
              }
              field.onChange(...args);
            }}
            onBlur={field.onBlur}
          />
        </Stack>
      )}
    />
  );
};
